import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { ClubNavIcon } from "../../layouts/clubNav";
import { AdminCard, StatCard, StatGrid } from "../../components/admin/AdminCard";
import { getLeaderboard, LeaderboardEntry, type LeaderboardWindow } from "../../services/quizzes";
import { getClubTeams, type Team } from "../../services/teams";
import { useAuth } from "../../context/AuthContext";

const LIMIT = 20;

function initials(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Deterministic gradient per name so avatars feel personal but stable.
const AVATAR_GRADIENTS = [
  "from-indigo-500 to-violet-500",
  "from-sky-500 to-cyan-500",
  "from-emerald-500 to-teal-500",
  "from-rose-500 to-pink-500",
  "from-amber-500 to-orange-500",
  "from-fuchsia-500 to-purple-500",
];
function gradientFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
}

// Podium visual tokens for ranks 1/2/3.
const PODIUM = [
  {
    medal: "🥇",
    ring: "ring-amber-300",
    plate: "bg-gradient-to-b from-amber-50 to-white border-amber-200",
    badge: "bg-amber-400 text-amber-950",
    bar: "from-amber-300 to-amber-400",
    height: "h-24 sm:h-28",
    avatar: "w-16 h-16 sm:w-20 sm:h-20 text-lg sm:text-xl",
  },
  {
    medal: "🥈",
    ring: "ring-slate-300",
    plate: "bg-gradient-to-b from-slate-50 to-white border-slate-200",
    badge: "bg-slate-300 text-slate-800",
    bar: "from-slate-300 to-slate-400",
    height: "h-16 sm:h-20",
    avatar: "w-14 h-14 sm:w-16 sm:h-16 text-base sm:text-lg",
  },
  {
    medal: "🥉",
    ring: "ring-amber-700/40",
    plate: "bg-gradient-to-b from-amber-50/60 to-white border-amber-200",
    badge: "bg-amber-700 text-amber-50",
    bar: "from-amber-600 to-amber-700",
    height: "h-12 sm:h-16",
    avatar: "w-14 h-14 sm:w-16 sm:h-16 text-base sm:text-lg",
  },
];

function Badges({ entry }: { entry: LeaderboardEntry }) {
  const items = [entry.team, entry.age_group, entry.club].filter(Boolean) as string[];
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {items.map((label, i) => (
        <span
          key={`${label}-${i}`}
          className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-[11px] font-medium text-slate-600 truncate max-w-[10rem]"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function PodiumColumn({
  entry,
  index,
  t,
}: {
  entry: LeaderboardEntry;
  index: number;
  t: TFunction;
}) {
  const cfg = PODIUM[index];
  // Center (rank 1) sits above the flanks via items-end on the row.
  const order = index === 0 ? "order-2" : index === 1 ? "order-1" : "order-3";
  return (
    <div className={`flex flex-col items-center ${order}`}>
      <div className="relative">
        <div
          className={`${cfg.avatar} rounded-full bg-gradient-to-br ${gradientFor(
            entry.user_id
          )} text-white font-bold flex items-center justify-center ring-4 ${cfg.ring} shadow-sm`}
        >
          {initials(entry.name)}
        </div>
        <span className="absolute -bottom-1 -right-1 text-xl sm:text-2xl drop-shadow-sm">{cfg.medal}</span>
      </div>
      <div className="mt-2 text-center px-1 w-full">
        <div className="font-semibold text-slate-800 text-xs sm:text-sm truncate">{entry.name}</div>
        <div className="text-indigo-600 font-extrabold tabular-nums text-base sm:text-lg leading-tight">
          {entry.total_score}
        </div>
        <div className="text-[10px] text-slate-500">
          {entry.quiz_count} {t("leaderboard.quizzes", "quiz-uri")}
        </div>
      </div>
      <div
        className={`mt-2 w-full rounded-t-xl border-t border-x ${cfg.plate} ${cfg.height} flex items-start justify-center pt-1.5`}
      >
        <span
          className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-extrabold ${cfg.badge}`}
        >
          {index + 1}
        </span>
      </div>
    </div>
  );
}

function ListRow({
  entry,
  rank,
  maxScore,
  t,
}: {
  entry: LeaderboardEntry;
  rank: number;
  maxScore: number;
  t: TFunction;
}) {
  const pct = maxScore > 0 ? Math.max(4, Math.round((entry.total_score / maxScore) * 100)) : 0;
  return (
    <div className="flex items-center gap-3 px-3 sm:px-4 py-3 hover:bg-slate-50 transition-colors">
      <div className="w-7 sm:w-8 text-center text-sm font-bold text-slate-400 tabular-nums flex-shrink-0">
        {rank}
      </div>
      <div
        className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradientFor(
          entry.user_id
        )} text-white text-sm font-bold flex items-center justify-center flex-shrink-0`}
      >
        {initials(entry.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800 text-sm truncate">{entry.name}</div>
        <Badges entry={entry} />
      </div>
      <div className="hidden sm:block w-28 flex-shrink-0">
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-indigo-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="text-right flex-shrink-0 w-16">
        <div className="text-base font-extrabold text-indigo-600 tabular-nums leading-tight">
          {entry.total_score}
        </div>
        <div className="text-[10px] text-slate-500 tabular-nums">
          {entry.quiz_count} {t("leaderboard.quizzes", "quiz-uri")}
        </div>
      </div>
    </div>
  );
}

export default function ClubLeaderboard() {
  const { t } = useTranslation();
  const { activeClubId } = useAuth();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * Two questions, not one.
   *
   * "Who is top of the club" is what this page answered. A coach standing in
   * front of twelve kids needs "who is top of THIS team", and in a club with
   * three hundred athletes the club board never shows their squad at all. And
   * all of it was all-time, which after a season means the ranking is settled:
   * a child who joined in March cannot appear however well they do, so the
   * board stops being something to play for.
   */
  const [scope, setScope] = useState<"club" | "team">("club");
  const [period, setPeriod] = useState<LeaderboardWindow>("monthly");
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamId, setTeamId] = useState<string>("");

  useEffect(() => {
    if (!activeClubId) return;
    let active = true;
    getClubTeams(activeClubId)
      .then((list) => {
        if (!active) return;
        setTeams(list);
        setTeamId((cur) => cur || list[0]?.id || "");
      })
      .catch(() => {
        /* The team picker simply stays empty; the club board still works. */
      });
    return () => {
      active = false;
    };
  }, [activeClubId]);

  useEffect(() => {
    if (!activeClubId) return;
    if (scope === "team" && !teamId) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        // Scoped to this club — the unscoped call ranked every athlete on the
        // platform, so unrelated clubs' athletes showed up here.
        const data = await getLeaderboard({
          limit: LIMIT,
          club_id: activeClubId,
          period,
          ...(scope === "team" ? { team_id: teamId } : {}),
        });
        if (active) setEntries(data);
      } catch (err) {
        console.error("Failed to load leaderboard:", err);
        if (active) setEntries([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [activeClubId, scope, period, teamId]);

  const PERIOD_LABELS: Record<LeaderboardWindow, string> = {
    daily: t("leaderboard.daily", "Azi"),
    weekly: t("leaderboard.weekly", "Săptămâna"),
    monthly: t("leaderboard.monthly", "Luna"),
    yearly: t("leaderboard.yearly", "Anul"),
    alltime: t("leaderboard.alltime", "Dintotdeauna"),
  };
  const PERIOD_ORDER: LeaderboardWindow[] = ["weekly", "monthly", "yearly", "alltime"];

  const top3 = useMemo(() => entries.slice(0, 3), [entries]);
  const rest = useMemo(() => entries.slice(3), [entries]);
  const maxScore = entries.length > 0 ? entries[0].total_score : 0;
  const totalQuizzes = useMemo(
    () => entries.reduce((sum, e) => sum + (e.quiz_count || 0), 0),
    [entries]
  );

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
        <AdminPageHeader
          title={t("leaderboard.title", "Clasament")}
          subtitle={
            scope === "team"
              ? t("leaderboard.explanationTeam", "Punctele din quiz-uri, pentru o singură echipă")
              : t("leaderboard.explanation", "Punctele acumulate din quiz-uri, pe tot clubul")
          }
          icon={<ClubNavIcon id="leaderboard" className="w-6 h-6" />}
        />

        {/* Scope + window. Two rows rather than one long filter bar: the first
            decides WHO is being ranked, the second OVER WHAT TIME, and mixing
            them into one strip made neither read as a choice. */}
        <AdminCard className="p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="inline-flex p-1 rounded-xl bg-slate-100">
              {(["club", "team"] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setScope(s)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                    scope === s ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {s === "club"
                    ? t("leaderboard.scopeClub", "Tot clubul")
                    : t("leaderboard.scopeTeam", "Pe echipă")}
                </button>
              ))}
            </div>

            {scope === "team" && (
              <select
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                className="px-3 py-2 text-sm font-semibold bg-white border rounded-xl border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {teams.length === 0 && <option value="">{t("leaderboard.noTeams", "Nicio echipă")}</option>}
                {teams.map((tm) => (
                  <option key={tm.id} value={tm.id}>
                    {tm.name}
                  </option>
                ))}
              </select>
            )}

            <div className="inline-flex p-1 ml-auto rounded-xl bg-slate-100">
              {PERIOD_ORDER.map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                    period === p ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {PERIOD_LABELS[p]}
                </button>
              ))}
            </div>
          </div>
        </AdminCard>

        {loading ? (
          <AdminCard className="p-10">
            <div className="text-center text-sm text-slate-500">{t("common.loading", "Se încarcă...")}</div>
          </AdminCard>
        ) : entries.length === 0 ? (
          <AdminCard className="p-10">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-3xl">
                🏆
              </div>
              <div className="mt-4 text-base font-semibold text-slate-700">
                {t("leaderboard.noEntries", "Niciun rezultat în această perioadă")}
              </div>
              {/* The old copy said "nobody has taken a quiz yet", which with a
                  window selected is simply wrong — and sent a club admin
                  looking for a problem when all that happened is that it is
                  Monday. */}
              <p className="max-w-sm mt-1 text-sm text-slate-500">
                {period === "alltime"
                  ? t("leaderboard.no_data", "Niciun quiz completat încă.")
                  : scope === "team"
                    ? t(
                        "leaderboard.no_data_team",
                        "Echipa nu a completat niciun quiz în perioada aleasă. Încearcă un interval mai lung."
                      )
                    : t(
                        "leaderboard.no_data_period",
                        "Nimeni din club nu a completat un quiz în perioada aleasă. Încearcă un interval mai lung."
                      )}
              </p>
            </div>
          </AdminCard>
        ) : (
          <>
            <StatGrid cols={3}>
              <StatCard
                label={t("leaderboard.rank", "Participanți")}
                value={entries.length}
                dotClass="bg-indigo-500"
              />
              <StatCard
                label={t("leaderboard.points", "Scor maxim")}
                value={maxScore}
                valueClass="text-indigo-600"
                dotClass="bg-amber-400"
              />
              <StatCard
                label={t("leaderboard.quizzes", "Quiz-uri finalizate")}
                value={totalQuizzes}
                dotClass="bg-emerald-500"
              />
            </StatGrid>

            {/* Podium */}
            {top3.length > 0 && (
              <AdminCard className="p-4 sm:p-6 overflow-hidden">
                <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-800 flex items-center gap-2">
                    <span>🏆</span>
                    {t("leaderboard.title", "Clasament")}
                  </h3>
                  <span className="text-xs text-slate-500">{t("leaderboard.top_n", "Top") + " " + LIMIT}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 sm:gap-4 items-end max-w-xl mx-auto">
                  {top3.map((entry, i) => (
                    <PodiumColumn key={entry.user_id} entry={entry} index={i} t={t} />
                  ))}
                </div>
              </AdminCard>
            )}

            {/* Ranked list */}
            <AdminCard className="overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-slate-800">
                  {t("leaderboard.rank", "Clasament complet")}
                </h3>
                <span className="text-xs text-slate-500">
                  {entries.length} {t("leaderboard.name", "participanți")}
                </span>
              </div>
              {rest.length > 0 ? (
                <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
                  {rest.map((entry, i) => (
                    <ListRow key={entry.user_id} entry={entry} rank={i + 4} maxScore={maxScore} t={t} />
                  ))}
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-sm text-slate-500">
                  {t("leaderboard.top_n", "Top") + " 3"}
                </div>
              )}
              <div className="px-4 py-3 border-t border-slate-100 text-center text-xs text-slate-500">
                {t("leaderboard.explanation", "Pe baza punctelor totale acumulate din toate quiz-urile")}
              </div>
            </AdminCard>
          </>
        )}
      </div>
    </ClubAdminLayout>
  );
}
